/**
 * Client Configuration Service
 * Handles client-specific data paths based on URL query parameters
 *
 * Usage:
 *   URL: https://example.com/app?client_type=lavagna
 *   This will load data from: {UPLOADS_API}/api/browse/traffix_all_in_one/lavagna/
 *
 * Default client is 'default' which uses the existing data structure
 */

import { getClientFileUrl } from './uploadApi';

// Default client identifier
const DEFAULT_CLIENT = 'default';

// Storage key for the auth token (used for API authentication)
const AUTH_TOKEN_KEY = 'auth_token';

// Cache the client type to avoid repeated URL parsing
let cachedClientType = null;

// Cache the auth token to avoid repeated URL parsing
let cachedAuthToken = null;

/**
 * Get the current client type from URL query parameters
 * @returns {string} The client type identifier
 */
export const getClientType = () => {
  if (cachedClientType !== null) {
    return cachedClientType;
  }

  try {
    const urlParams = new URLSearchParams(window.location.search);
    const clientType = urlParams.get('client_type');

    // Validate and sanitize the client type (only alphanumeric and underscores)
    if (clientType && /^[a-zA-Z0-9_-]+$/.test(clientType)) {
      cachedClientType = clientType;
      localStorage.setItem('client_type', clientType);

      // Remove client_type from URL so it doesn't persist in the address bar
      urlParams.delete('client_type');
      const newSearch = urlParams.toString();
      const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash;
      window.history.replaceState({}, '', newUrl);
    } else {
      // Fallback: check localStorage for client_type
      const storedClientType = localStorage.getItem('client_type');
      if (storedClientType && /^[a-zA-Z0-9_-]+$/.test(storedClientType)) {
        cachedClientType = storedClientType;
      } else {
        cachedClientType = DEFAULT_CLIENT;
      }
    }
  } catch (error) {
    console.warn('Error parsing client_type:', error);
    cachedClientType = DEFAULT_CLIENT;
  }

  return cachedClientType;
};

/**
 * Get the current auth token from URL query parameters or localStorage
 * @returns {string|null} The auth token
 */
export const getAuthToken = () => {
  if (cachedAuthToken !== null) {
    return cachedAuthToken;
  }

  try {
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get('auth_token');

    if (urlToken) {
      cachedAuthToken = urlToken;
      localStorage.setItem(AUTH_TOKEN_KEY, urlToken);

      // Remove auth_token from URL so it doesn't persist in the address bar
      urlParams.delete('auth_token');
      const newSearch = urlParams.toString();
      const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash;
      window.history.replaceState({}, '', newUrl);
    } else {
      cachedAuthToken = localStorage.getItem(AUTH_TOKEN_KEY);
    }
  } catch (error) {
    console.warn('Error parsing auth_token:', error);
    cachedAuthToken = null;
  }

  return cachedAuthToken;
};

/**
 * Set the auth token in cache and localStorage
 * @param {string} token
 */
export const setAuthToken = (token) => {
  cachedAuthToken = token;
  if (token) {
    localStorage.setItem(AUTH_TOKEN_KEY, token);
  } else {
    localStorage.removeItem(AUTH_TOKEN_KEY);
  }
};

/**
 * Reset the cached client type (useful for testing or route changes)
 */
export const resetClientCache = () => {
  cachedClientType = null;
};

/**
 * Check if using the default client
 * @returns {boolean} True if using default client
 */
export const isDefaultClient = () => {
  return getClientType() === DEFAULT_CLIENT;
};

/**
 * Get the base data path for the current client (remote API URL)
 * @returns {string} Base URL for client data on the uploads API
 */
export const getClientDataPath = () => {
  const clientType = getClientType();
  return getClientFileUrl(clientType, '');
};

/**
 * Build a full URL for a client-specific resource on the remote API
 * @param {string} relativePath - The relative path to the resource (relative to client folder)
 * @returns {string} Full API URL
 */
export const getClientPath = (relativePath) => {
  const clientType = getClientType();
  const cleanPath = relativePath.startsWith('/') ? relativePath.slice(1) : relativePath;
  return getClientFileUrl(clientType, cleanPath);
};

/**
 * Client-specific path generators for different data types
 * All paths are relative to the client folder on the remote uploads API
 */
export const clientPaths = {
  // GeoJSON network data
  networkOffset: () => getClientPath('/network_offset.geojson'),
  archiZona: () => getClientPath('/archi_zona.geojson'),
  mapGeoJson: () => getClientPath('/Map.geojson'),

  // Traffic/metrics data
  metricsCSV: () => getClientPath('/metrics.csv'),
  trafficCSV: () => getClientPath('/Traffic_5min.csv'),
  metricsJSON: () => getClientPath('/metrics.json'),

  // Environmental data
  ambientaliDaily: (date) => getClientPath(`/ambientali/${date}.json`),
  ambientaliIndex: () => getClientPath('/ambientali/media_giornaliera.json'),
  environmentHourly: () => getClientPath('/Environment_1h.json'),

  // Noise/Audio data
  noiseDaily: (date) => getClientPath(`/geojson/${date}.json`),
  noiseIndex: () => getClientPath('/geojson/json_index.json'),
  detectedSounds: () => getClientPath('/detected_sounds.json'),
  noiseMinute: () => getClientPath('/Noise_1min.json'),

  // IMU/Vibration data
  imuDaily: (filename) => getClientPath(`/imu/${filename}`),
  imuIndex: () => getClientPath('/imu/index.json'),
  vibrationMinute: () => getClientPath('/Vibration_1min.json'),

  // Other data
  animatedVehicles: () => getClientPath('/animated_vehicles.geojson'),
  trips: () => getClientPath('/trips_deckgl.json'),
  pointCloud3D: () => getClientPath('/NuvolaPunti3d.json'),
  buildings: () => getClientPath('/buildings_genova.json'),

  // Traffic marker data (strade/varchi)
  stradeGeo: () => getClientPath('/strade/streets_geo.json'),
  stradeTimeseries: () => getClientPath('/strade/timeseries_long.json'),
  varchiGeo: () => getClientPath('/varchi/varchi_geo.json'),
  varchiTimeseries: () => getClientPath('/varchi/varchi_timeseries_long.json'),

  // Videos
  video: (filename) => getClientPath(`/videos/${filename}`),

  // Icons
  stradeIcon: () => getClientPath('/strade/strade.png'),
  varchiIcon: () => getClientPath('/varchi/varchi.png'),

  // Simfix scenario data
  simfixScenarios: () => getClientPath('/grafi_per_scenario.json'),

  // Simfix area circle data
  simfixArea: () => getClientPath('/simfix_area.json'),

  // Assignx data
  assignxFlows: () => getClientPath('/assignx_flows.geojson'),
  assignxNetwork: () => getClientPath('/assignx_network.geojson'),

  // SignalFix data
  signalFixCarGps: () => getClientPath('/signal_fix/Car_gps.geojson'),
  signalFixSegmentiRms: () => getClientPath('/signal_fix/segmenti_rms.geojson'),
  signalFixPlyLocations: () => getClientPath('/signal_fix/ply_locations.json'),
  signalFixCarPathInfo: () => getClientPath('/signal_fix/car_path_info.json'),
};

/**
 * Get client configuration metadata
 * @returns {object} Client configuration object
 */
export const getClientConfig = () => {
  return {
    clientType: getClientType(),
    isDefault: isDefaultClient(),
    basePath: getClientDataPath(),
  };
};

export default {
  getClientType,
  getAuthToken,
  setAuthToken,
  resetClientCache,
  isDefaultClient,
  getClientDataPath,
  getClientPath,
  clientPaths,
  getClientConfig,
};
